import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { money, pct1 } from "./lux";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Plus, Trash, PencilSimple, Key, CheckCircle, ChartLineUp } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SalespeoplePanel() {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", aliases: "" });
  const [edit, setEdit] = useState(null); // salesperson being edited
  const [editForm, setEditForm] = useState({ name: "", email: "", aliases: "" });
  const [login, setLogin] = useState(null); // salesperson whose login is being set
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const [perf, setPerf] = useState([]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/salespeople"); setPeople(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Last 30 days of performance, from the same figures /sales-performance uses.
  useEffect(() => {
    const day = (offset) => new Date(Date.now() - offset * 86400000).toISOString().slice(0, 10);
    api.get("/sales-performance/salespeople", { params: { from: day(30), to: day(0) } })
      .then((r) => setPerf(r.data?.rows || []))
      .catch(() => setPerf([]));
  }, []);

  const perfFor = useMemo(() => {
    const map = {};
    for (const r of perf) map[r.salesperson.toLowerCase()] = r;
    return map;
  }, [perf]);

  const addPerson = async () => {
    if (!addForm.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api.post("/salespeople", {
        name: addForm.name.trim(),
        email: addForm.email.trim(),
        aliases: addForm.aliases.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Salesperson added");
      setAddOpen(false); setAddForm({ name: "", email: "", aliases: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const openEdit = (p) => { setEdit(p); setEditForm({ name: p.name, email: p.email || "", aliases: (p.aliases || []).join(", ") }); };
  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api.put(`/salespeople/${edit.id}`, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        active: edit.active !== false,
        aliases: editForm.aliases.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Salesperson updated");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const removePerson = async (p) => {
    if (!window.confirm(`Remove ${p.name}? This also revokes their login if one exists.`)) return;
    try { await api.delete(`/salespeople/${p.id}`); toast.success("Salesperson removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openLogin = (p) => { setLogin(p); setLoginForm({ email: p.email || "", password: "" }); };
  const saveLogin = async () => {
    if (!loginForm.email.trim() || loginForm.password.length < 6) { toast.error("Enter an email and a password of at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post(`/salespeople/${login.id}/login`, { email: loginForm.email.trim(), password: loginForm.password });
      toast.success(`Login ${login.has_login ? "updated" : "created"} for ${login.name}`);
      setLogin(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="salespeople-panel">
      <div className="flex items-center justify-between">
        <div className="label-mono">Salespeople — manage the team & their logins</div>
        <Button size="sm" onClick={() => setAddOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-salesperson-button"><Plus size={14} className="mr-1.5" />Add Salesperson</Button>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-4 py-3 label-mono font-normal">Name</th>
              <th className="text-left px-4 py-3 label-mono font-normal">Login email</th>
              <th className="text-left px-4 py-3 label-mono font-normal">Login</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Bookings 30d</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Revenue 30d</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Close %</th>
              <th className="text-right px-4 py-3 label-mono font-normal w-72">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">Loading…</td></tr>
              : people.length === 0 ? <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No salespeople yet. Click "Add Salesperson".</td></tr>
              : people.map((p) => (
                <tr key={p.id} className="border-b border-border/40 hover:bg-secondary/20" data-testid={`salesperson-row-${p.id}`}>
                  <td className="px-4 py-3 font-heading">
                    {p.name}
                    {(p.aliases || []).length > 0 && (
                      <div className="text-xs font-normal text-muted-foreground mt-0.5" data-testid={`salesperson-aliases-${p.id}`}>
                        also: {p.aliases.join(", ")}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{p.email || "—"}</td>
                  <td className="px-4 py-3">
                    {p.has_login
                      ? <span className="inline-flex items-center gap-1 text-emerald-400 text-xs" data-testid={`salesperson-haslogin-${p.id}`}><CheckCircle size={14} />Active</span>
                      : <span className="text-xs text-muted-foreground">No login</span>}
                  </td>
                  <td className="px-4 py-3 text-right tnum" data-testid={`salesperson-bookings-${p.id}`}>
                    {perfFor[p.name.toLowerCase()]?.bookings ?? 0}
                  </td>
                  <td className="px-4 py-3 text-right tnum text-primary" data-testid={`salesperson-revenue-${p.id}`}>
                    {money(perfFor[p.name.toLowerCase()]?.revenue || 0)}
                  </td>
                  <td className="px-4 py-3 text-right tnum" data-testid={`salesperson-close-${p.id}`}>
                    {perfFor[p.name.toLowerCase()]?.lead_close?.of ? pct1(perfFor[p.name.toLowerCase()].lead_close.rate) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link
                      to={`/sales-performance?person=${encodeURIComponent(p.name)}`}
                      className="inline-flex items-center h-8 px-3 mr-1.5 rounded-md border border-border text-xs hover:bg-accent"
                      title="Open this person's performance detail"
                      data-testid={`salesperson-performance-${p.id}`}
                    >
                      <ChartLineUp size={12} className="mr-1 text-primary" />Performance
                    </Link>
                    <Button size="sm" variant="outline" onClick={() => openLogin(p)} data-testid={`salesperson-login-${p.id}`}><Key size={12} className="mr-1" />{p.has_login ? "Manage login" : "Set login"}</Button>
                    <Button size="sm" variant="outline" className="ml-1.5" onClick={() => openEdit(p)} data-testid={`salesperson-edit-${p.id}`}><PencilSimple size={12} className="mr-1" />Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => removePerson(p)} data-testid={`salesperson-delete-${p.id}`}><Trash size={14} className="text-destructive" /></Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Add */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="add-salesperson-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Add Salesperson</DialogTitle><DialogDescription>They'll appear in the booking Salesperson dropdown. You can set their login next.</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="label-mono mb-1.5 block">Name *</Label><Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="bg-input border-border" data-testid="salesperson-name-input" /></div>
            <div><Label className="label-mono mb-1.5 block">Email (optional)</Label><Input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} className="bg-input border-border" data-testid="salesperson-email-input" /></div>
            <div>
              <Label className="label-mono mb-1.5 block">Aliases (optional)</Label>
              <Input value={addForm.aliases} onChange={(e) => setAddForm({ ...addForm, aliases: e.target.value })} placeholder="e.g. Shayaan, Shaayan, Shayyan" className="bg-input border-border" data-testid="salesperson-aliases-input" />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated. Historical misspellings &amp; nicknames — bookings written with any alias will show up in this salesperson&apos;s &quot;My Bookings&quot;.</p>
            </div>
          </div>
          <DialogFooter className="mt-4"><Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button><Button onClick={addPerson} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="salesperson-save-button">{saving ? "Saving…" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="edit-salesperson-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Edit Salesperson</DialogTitle><DialogDescription>Renaming updates their existing bookings, leads and login automatically.</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="label-mono mb-1.5 block">Name *</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="bg-input border-border" data-testid="edit-salesperson-name-input" /></div>
            <div><Label className="label-mono mb-1.5 block">Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="bg-input border-border" data-testid="edit-salesperson-email-input" /></div>
            <div>
              <Label className="label-mono mb-1.5 block">Aliases</Label>
              <Input value={editForm.aliases} onChange={(e) => setEditForm({ ...editForm, aliases: e.target.value })} placeholder="e.g. Shayaan, Shaayan, Shayyan" className="bg-input border-border" data-testid="edit-salesperson-aliases-input" />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated. Case-insensitive &amp; word-boundary aware — matches lone tokens as well as compound entries like &quot;Runner/Leon&quot;.</p>
            </div>
          </div>
          <DialogFooter className="mt-4"><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={saveEdit} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="edit-salesperson-save-button">{saving ? "Saving…" : "Save changes"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set / manage login */}
      <Dialog open={!!login} onOpenChange={(o) => !o && setLogin(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="salesperson-login-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{login?.has_login ? "Manage login" : "Set login"} — {login?.name}</DialogTitle><DialogDescription>Creates a Booking Agent account tied to this salesperson. They'll see only their own bookings. Share the email + password with them; they can change it after signing in.</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="label-mono mb-1.5 block">Login email *</Label><Input type="email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className="bg-input border-border" data-testid="salesperson-login-email" /></div>
            <div><Label className="label-mono mb-1.5 block">{login?.has_login ? "New password" : "Temporary password"} *</Label><Input type="text" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="min 6 characters" className="bg-input border-border" data-testid="salesperson-login-password" /></div>
          </div>
          <DialogFooter className="mt-4"><Button variant="outline" onClick={() => setLogin(null)}>Cancel</Button><Button onClick={saveLogin} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="salesperson-login-save">{saving ? "Saving…" : login?.has_login ? "Update login" : "Create login"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
